import { useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { Card } from '../../components/Card';
import { CmsSectionEditor } from '../../components/CmsSectionEditor';
import { useAdminCms, saveCmsSection, clearCmsSection, type AdminCmsSection } from '../../api/cms';
import { CMS_REGISTRY } from '../../content/registry';
import { SINGLE_FALLBACKS, LIST_FALLBACKS } from '../../content/fallbacks';

/**
 * Marketing CMS editor (platform admin) — §WS5b, extended to full coverage.
 * Registry-driven (`content/registry.ts`): every page group and section in the
 * registry renders here, grouped under a collapsible `<details>` per page,
 * whether or not a DB row exists yet — an unsaved section shows the built-in
 * fallback copy (from `content/fallbacks.ts`) with a "Using default copy"
 * badge; once saved it shows "Customized" + a "Reset to default" button.
 * Saving/resetting invalidates the public `cms` query so the live marketing
 * pages pick up the change immediately. No raw HTML, no page builder.
 */
export function Cms() {
  const { data: sections = [], isLoading } = useAdminCms();
  const qc = useQueryClient();
  const byKey = new Map<string, AdminCmsSection>(sections.map((s) => [s.sectionKey, s]));

  async function onSaved() {
    await qc.invalidateQueries({ queryKey: ['admin-cms'] });
    await qc.invalidateQueries({ queryKey: ['cms'] });
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-3xl mx-auto">
      <PageHeader
        eyebrow="Platform"
        title="Marketing content"
        subtitle="Edit every public marketing page (itcitysoft.com / possignin.com / pricing / features / about / blog), plus the shared default template resellers inherit before their own overrides. Changes go live immediately."
      />
      {isLoading ? (
        <Card>
          <div className="p-6 space-y-3" aria-busy="true">
            <div className="skeleton h-5 w-40 rounded" />
            <div className="skeleton h-24 rounded" />
            <div className="skeleton h-24 rounded" />
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {CMS_REGISTRY.map((page) => (
            <details key={page.key} className="group rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white/60 dark:bg-zinc-900/30" open>
              <summary className="cursor-pointer list-none px-4 sm:px-5 py-3.5 flex items-center justify-between gap-3">
                <span className="font-semibold text-zinc-950 dark:text-zinc-50">{page.label}</span>
                <span className="text-xs text-zinc-400 dark:text-zinc-500">
                  {page.sections.length} section{page.sections.length === 1 ? '' : 's'}
                </span>
              </summary>
              <div className="px-4 sm:px-5 pb-5 space-y-4">
                {page.sections.map((section) => (
                  <CmsSectionEditor
                    key={section.key}
                    spec={section}
                    existing={byKey.get(section.key)}
                    fallbackSingle={SINGLE_FALLBACKS[section.key] ?? {}}
                    fallbackList={LIST_FALLBACKS[section.key] ?? []}
                    save={saveCmsSection}
                    clear={clearCmsSection}
                    onSaved={onSaved}
                  />
                ))}
              </div>
            </details>
          ))}
        </div>
      )}
    </div>
  );
}
